import React, { useRef, useState } from "react";
import Slider from "react-slick";
import { LocationOn } from "@mui/icons-material";
import {
  Card,
  Typography,
  Box,
  Button,
  CardMedia,
  CardContent,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import Skeleton from "@mui/material/Skeleton";

const GiooGridView = ({
  image,
  images = [],
  name,
  totalPrice,
  totalAmount,
  price,
  id,
  address,
  nearBy,
  amenity,
  hideAmenities,
  hideNearBy,
  soldStatus,
  sold_status,
}) => {
  const isSold = soldStatus === 1 || sold_status === 1;
  const nav = useNavigate();
  const previewSliderRef = useRef(null);
  const sliderRef = useRef(null);
  const [imgOpen, setImgOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imgPressed, setImgPressed] = useState(false);

  const cardSliderSettings = {
    dots: false,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    adaptiveHeight: false,
  };

  const safeNearBy = Array.isArray(nearBy) ? nearBy : [];
  const safeAmenity = Array.isArray(amenity) ? amenity : [];
  const limitToThree = (arr) => (Array.isArray(arr) ? arr.slice(0, 3) : []);
  const nearByList = limitToThree(safeNearBy);
  const amenityList = limitToThree(safeAmenity);

  // Build full image list
  const allImages =
    Array.isArray(images) && images.length > 0
      ? images
      : image
        ? [image]
        : ["/assets/images/placeholder_img.png"];

  const showNavArrows = allImages.length > 1;

  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredVideo, setHoveredVideo] = useState(null);

  const togglePlay = (e, src) => {
    e.stopPropagation();
    const video = document.querySelector(`video[src="${src}"]`);
    if (video) {
      if (video.paused) {
        // Pause other playing video if any
        if (playingVideo && playingVideo !== src) {
          const otherVideo = document.querySelector(`video[src="${playingVideo}"]`);
          if (otherVideo) otherVideo.pause();
        }
        video.play();
        setPlayingVideo(src);
      } else {
        video.pause();
        setPlayingVideo(null);
      }
    }
  };

  const isVideo = (url) => {
    if (typeof url !== 'string') return false;
    return url.match(/\.(mp4|webm|mkv|mov|avi)$/i);
  };

  const handleOpenPreview = (index = 0) => {
    setActiveIndex(index);
    setImgOpen(true);
    setTimeout(() => {
      previewSliderRef.current?.slickGoTo(index);
    }, 100);
  };

  return (
    <>
      {/* MULTI-IMAGE POPUP DIALOG */}
      <Dialog
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", bgcolor: "rgba(0,0,0,0.85)" },
        }}
      >
        <IconButton
          onClick={() => setImgOpen(false)}
          sx={{ position: "absolute", top: { xs: 10, sm: 16 }, right: { xs: 10, sm: 20 }, zIndex: 10, color: "#fff", bgcolor: "rgba(0,0,0,0.4)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: "relative", height: { xs: 300, sm: 480, md: 620 }, overflow: "hidden" }}>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickPrev()}
                sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}
            <Slider ref={previewSliderRef} initialSlide={activeIndex} beforeChange={(old, next) => setActiveIndex(next)} dots infinite speed={400} slidesToShow={1} slidesToScroll={1} arrows={false}>
              {allImages.map((img, i) => (
                <Box key={i}>
                  <Box sx={{ height: { xs: 300, sm: 480, md: 620 }, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.7)", p: 1 }}>
                    {isVideo(img) ? (
                      <video 
                        src={img} 
                        controls 
                        playsInline 
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nopictureinpicture"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }} 
                      />
                    ) : (
                      <img src={img} alt={`${name || ""} ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Slider>
            {showNavArrows && (
              <IconButton
                onClick={() => previewSliderRef.current?.slickNext()}
                sx={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 3, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
            {showNavArrows && (
              <Box sx={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", bgcolor: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "12px", px: 1.5, py: 0.3, fontSize: 13, pointerEvents: "none", zIndex: 3 }}>
                {activeIndex + 1} / {allImages.length}
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* CARD */}
      <Card
        onClick={() => nav(`/gioo-plots/details/${id}`)}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          mx: { xs: 0.5, sm: 0.5, md: 1 },
          p: { xs: 1, sm: 1.2, md: 1.5 },
          borderRadius: { xs: 2, sm: 3, md: 4 },
          position: "relative",
          backgroundImage: `url("/assets/images/gioo_grid_bg.png")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-5px) scale(1.01)",
            boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box onClick={(e) => e.stopPropagation()} sx={{ position: "relative" }}>
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 13,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            {allImages.length > 1 ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  "& .slick-slider, & .slick-list, & .slick-track, & .slick-slide > div": {
                    height: "100% !important",
                  },
                }}
              >
              <Slider ref={sliderRef} {...cardSliderSettings} beforeChange={() => {
                if (playingVideo) {
                  const video = document.querySelector(`video[src="${playingVideo}"]`);
                  if (video) video.pause();
                  setPlayingVideo(null);
                }
              }}>
                {allImages.map((img, i) => (
                  <Box key={i}>
                    {isVideo(img) ? (
                      <Box
                        onMouseEnter={() => setHoveredVideo(img)}
                        onMouseLeave={() => setHoveredVideo(null)}
                        sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9" }}
                      >
                        <CardMedia
                          component="video"
                          src={img}
                          playsInline
                          disablePictureInPicture
                          disableRemotePlayback
                          controlsList="nopictureinpicture"
                          onClick={(e) => { e.stopPropagation(); togglePlay(e, img); }}
                          sx={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            objectFit: "cover",
                            borderRadius: 3,
                            cursor: "pointer",
                            transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                            "&:hover": { transform: "scale(1.04)" },
                          }}
                        />
                        {(playingVideo !== img || hoveredVideo === img) && (
                          <IconButton
                            onClick={(e) => togglePlay(e, img)}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              borderRadius: "4px",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                            }}
                          >
                            {playingVideo === img ? <PauseIcon /> : <PlayArrowIcon />}
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <CardMedia
                        component="img"
                        image={img}
                        alt={name}
                        onClick={(e) => { e.stopPropagation(); handleOpenPreview(i); }}
                        sx={{
                          width: "100%",
                          aspectRatio: "16 / 9",
                          objectFit: "cover",
                          borderRadius: 3,
                          cursor: "zoom-in",
                          transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                          "&:hover": { transform: "scale(1.04)" },
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Slider>
              {/* Card slider arrows */}
              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickPrev(); }}
                sx={{
                  position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }, display: { xs: "none", sm: "flex" }
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                onClick={(e) => { e.stopPropagation(); sliderRef.current?.slickNext(); }}
                sx={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  zIndex: 2, bgcolor: "rgba(255,255,255,0.7)", p: 0.5,
                  "&:hover": { bgcolor: "white" }, display: { xs: "none", sm: "flex" }
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box sx={{ position: "absolute", bottom: 8, right: 8, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: "6px", px: 1, py: 0.3, pointerEvents: "none", zIndex: 2 }}>
                +{allImages.length - 1} more
              </Box>
            </Box>
          ) : (
            isVideo(allImages[0]) ? (
              <Box
                onMouseEnter={() => setHoveredVideo(allImages[0])}
                onMouseLeave={() => setHoveredVideo(null)}
                sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9" }}
              >
                <CardMedia
                  component="video"
                  src={allImages[0]}
                  playsInline
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nopictureinpicture"
                  onClick={(e) => { e.stopPropagation(); togglePlay(e, allImages[0]); }}
                  sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                    transform: imgPressed ? "scale(0.92)" : "scale(1)",
                    boxShadow: imgPressed ? "0 2px 8px rgba(0,0,0,0.18)" : "none",
                    "&:hover": { transform: imgPressed ? "scale(0.92)" : "scale(1.04)" },
                  }}
                />
                {(playingVideo !== allImages[0] || hoveredVideo === allImages[0]) && (
                  <IconButton
                    onClick={(e) => togglePlay(e, allImages[0])}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      bgcolor: "rgba(0,0,0,0.5)",
                      color: "white",
                      borderRadius: "4px",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                    }}
                  >
                    {playingVideo === allImages[0] ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                )}
              </Box>
            ) : (
              <CardMedia
                component="img"
                image={allImages[0]}
                alt={name}
                onClick={(e) => { e.stopPropagation(); handleOpenPreview(0); }}
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  objectFit: "cover",
                  borderRadius: 3,
                  cursor: "zoom-in",
                  transition: "transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease",
                  transform: imgPressed ? "scale(0.92)" : "scale(1)",
                  boxShadow: imgPressed ? "0 2px 8px rgba(0,0,0,0.18)" : "none",
                  "&:hover": { transform: imgPressed ? "scale(0.92)" : "scale(1.04)" },
                }}
              />
            )
          )}
          </Box>
        </Box>

        <CardContent sx={{ flexGrow: 1, p: { xs: 1, sm: 1.2, md: 1.5 }, display: "flex", flexDirection: "column", opacity: isSold ? 0.65 : 1 }}>
          <Box display="flex" justifyContent="space-between">
            <Typography
              fontWeight={600}
              fontSize={{ xs: 14, sm: 15, md: 16 }}
              sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}
            >
              {name}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" mt={0.5} mb={1}>
            <LocationOn sx={{ fontSize: 18, color: "#9DC354", mr: 0.5 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}
            >
              {address || ""}
            </Typography>
          </Box>

          <Divider sx={{ borderStyle: "dashed", borderColor: "secondary.light", borderWidth: 1, display: { xs: "none", sm: "block" } }} />

          {totalAmount && (
            <Box sx={{ mt: 1, mb: 0.5, px: 0.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 16, md: 17, lg: 18 }, color: "#2E7D32", display: "flex", alignItems: "center" }}>
                <CurrencyRupeeIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 }, mr: 0.3 }} />
                {Number(totalAmount).toLocaleString("en-IN")}
                <Typography component="span" sx={{ fontSize: 13, color: "text.secondary", ml: 1, fontWeight: 500 }}>Total Price</Typography>
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, width: "100%", gap: { xs: 1, sm: 0 } }}>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", whiteSpace: { xs: "normal", sm: "nowrap" } }}>
                <CurrencyRupeeIcon sx={{ fontSize: { xs: 14, sm: 13 }, color: "#9DC354", flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14, md: 14, lg: 14 } }}>{price || ""}</Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 11, md: 11 }, color: "text.secondary" }}>/Sqft</Typography>
              </Box>
              <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 11 }, color: "text.secondary", textAlign: "left" }}>Per Sq.ft</Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", whiteSpace: { xs: "normal", sm: "nowrap" } }}>
                <OpenWithIcon sx={{ fontSize: { xs: 14, sm: 13 }, color: "#9DC354", flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, fontSize: { xs: 13, sm: 14, md: 14, lg: 14 } }}>{totalPrice || ""}</Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 12 }, color: "text.secondary" }}>Sq.ft</Typography>
              </Box>
              <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 12 }, color: "text.secondary", textAlign: "left" }}>Total Area</Typography>
            </Box>
          </Box>

          <Divider sx={{ borderStyle: "dashed", borderColor: "secondary.light", borderWidth: 1, display: { xs: "none", sm: "block" } }} />

          {!hideNearBy && (
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 0.5, sm: 1 }, mt: 1, mb: 1 }}>
                <Typography sx={{ fontWeight: 500, fontSize: { xs: 11, sm: 12, md: 13 }, lineHeight: "22px", whiteSpace: "nowrap", flexShrink: 0 }}>Amenities:</Typography>
                <Typography color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", fontSize: { xs: 11, sm: 12, md: 13 } }}>
                  {amenityList?.length > 0
                    ? amenityList.map((item, index) => (
                      <span key={item.id}>{item.title}{index !== amenityList.length - 1 && " • "}</span>
                    ))
                    : "No amenities"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 0.5, sm: 1 }, mt: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: { xs: 11, sm: 12, md: 13 }, lineHeight: "22px", whiteSpace: "nowrap", flexShrink: 0 }}>Nearby:</Typography>
                <Typography color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", fontSize: { xs: 11, sm: 12, md: 13 } }}>
                  {nearByList?.length > 0
                    ? nearByList.map((item, index) => (
                      <span key={item.id}>{item.title}{index !== nearByList.length - 1 && " • "}</span>
                    ))
                    : "No nearby locations"}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>

        <Button
          variant="contained"
          sx={{ textTransform: "none", bgcolor: "#FDBA15", color: "black", "&:hover": { bgcolor: "#e3a30f" }, borderRadius: 20, fontWeight: 500, mt: 1, mb: { xs: 0.5, sm: 0 }, mx: { xs: 0, sm: 0.5, md: 0 }, py: { xs: 0.7, sm: 0.8 }, fontSize: { xs: "0.85rem", sm: "0.9rem" }, opacity: isSold ? 0.65 : 1 }}
          onClick={(e) => { e.stopPropagation(); nav(`/gioo-plots/details/${id}`); }}
        >
          View Details
        </Button>
      </Card>
    </>
  );
};

export default GiooGridView;
